import { isCorpTenant, useUserContext } from "@capone/common";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import {
  CorporateTypography,
  PagedCardCarousel,
  useDeviceTypes,
} from "halifax";
import React, { useMemo, useState } from "react";
import { InView } from "react-intersection-observer";
import { CorpSessionInfo, RewardsAccount } from "redmond";
import { config } from "../../../api/config";
import { trackEvent } from "../../../api/v1/trackEvent";
import VALUE_PROP_CARDS, { ImagePosition, ValuePropCard } from "./constants";
import "./styles.scss";

const ValuePropCard = ({
  imageUrl,
  imagePosition,
  description,
  matchesMobile,
}: {
  imageUrl: string;
  imagePosition?: ImagePosition;
  description: JSX.Element;
  matchesMobile: boolean;
}) => {
  const dynamicImagePosition = matchesMobile
    ? imagePosition?.mobile
    : imagePosition?.desktop;

  const getTextContent = (element: JSX.Element | string | number): string => {
    if (typeof element === "string") return element;
    if (typeof element === "number") return String(element);
    if (
      element &&
      typeof element === "object" &&
      "props" in element &&
      element.props?.children
    ) {
      if (Array.isArray(element.props.children)) {
        return element.props.children
          .map((child: any) =>
            typeof child === "string" || typeof child === "number"
              ? String(child)
              : child?.props?.children || ""
          )
          .join("");
      }
      return getTextContent(element.props.children);
    }
    return "";
  };

  const descriptionText = getTextContent(description);

  return (
    <Box
      className="value-prop-card"
      role="article"
      aria-label={descriptionText}
      tabIndex={0}
    >
      <Box
        style={{
          backgroundImage: `url(${imageUrl})`,
          backgroundPositionX: dynamicImagePosition || `center`,
        }}
        className="value-prop-image"
        role="img"
        aria-hidden="true"
      />
      <CorporateTypography
        variant={matchesMobile ? "body1" : "h5"}
        fontWeight={matchesMobile ? "regular" : "light"}
      >
        {description}
      </CorporateTypography>
    </Box>
  );
};

const getDynamicCashbackDescription = (
  currentAccount: RewardsAccount | undefined
): JSX.Element => {
  if (!currentAccount) {
    return (
      <>
        Enjoy <strong>unlimited 5% cash back</strong> on hotels & cars.
      </>
    );
  }

  const { hotelsMultiplier } = currentAccount.earn;
  const { currencyDescription, currency } = currentAccount.rewardsBalance;

  const isCash =
    currencyDescription?.toLowerCase().includes("cash") || currency === "$";

  if (isCash) {
    return (
      <>
        Enjoy <strong>unlimited {hotelsMultiplier}% cash back</strong> on hotels
        & cars.
      </>
    );
  }
  return (
    <>
      Enjoy <strong>unlimited {hotelsMultiplier}x miles</strong> on hotels &
      cars.
    </>
  );
};

interface ValuePropCarouselProps {
  currentAccount?: RewardsAccount;
}

export const ValuePropCarousel = ({
  currentAccount,
}: ValuePropCarouselProps) => {
  const isCorporate = isCorpTenant(config.TENANT);
  if (!isCorporate) {
    return null;
  }
  const { matchesMobile, matchesDesktop, matchesLargeDesktop, matchesTablet } =
    useDeviceTypes();
  const { sessionInfo } = useUserContext("capone-corporate");
  const isPrimary =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "PR";

  const [viewedTracked, setViewedTracked] = useState(false);
  const trackedPages = useMemo(() => new Set<string>(), []);

  const cardItems = useMemo(() => {
    const cards = VALUE_PROP_CARDS.filter(
      (card) => isPrimary || !card.isPrimary
    );

    if (currentAccount) {
      return cards.map((card) => {
        if (card.key === "cashback-value-prop") {
          return {
            ...card,
            description: getDynamicCashbackDescription(currentAccount),
          };
        }
        return card;
      });
    }

    return cards;
  }, [isPrimary, currentAccount]);

  return (
    <Box
      data-testid="corp-value-prop-carousel"
      className={clsx("value-prop-carousel-root", {
        mobile: matchesMobile,
        smallScreen: (matchesDesktop && !matchesLargeDesktop) || matchesTablet,
        extraBottomSpacing: !isPrimary,
      })}
    >
      <CorporateTypography
        variant={matchesMobile ? "h4" : "h2"}
        fontWeight={matchesMobile ? "regular" : "light"}
        className="title"
        id="benefits-carousel-title"
      >
        {isPrimary
          ? "Access powerful tools and benefits for your business"
          : "Booking business travel doesn't have to feel like work"}
      </CorporateTypography>
      <Box className="value-prop-carousel-wrapper">
        <InView
          as="div"
          onChange={(inView) => {
            if (inView && !viewedTracked) {
              trackEvent({
                eventName: "viewed_home_page_value_prop_carousel",
                properties: {
                  pages: cardItems.map((c) => c.key),
                },
              });
              setViewedTracked(true);
            }
          }}
          threshold={1}
        >
          <PagedCardCarousel
            itemsPerPage={matchesMobile ? 2 : 3}
            pageClassName="value-prop-carousel-page"
            cardContents={cardItems.map((item) => (
              <ValuePropCard
                key={item.key}
                imageUrl={item.imageUrl}
                imagePosition={item.imagePosition}
                matchesMobile={matchesMobile}
                description={item.description}
              />
            ))}
            trackingData={cardItems.map((item) => ({
              type: item.key,
            }))}
            onPageViewed={(pageKey, tracking) => {
              if (!trackedPages.has(pageKey)) {
                trackEvent({
                  eventName: "viewed_home_page_value_props",
                  properties: {
                    page_items: tracking.map((t) => t.type),
                  },
                });
                trackedPages.add(pageKey);
              }
            }}
          />
        </InView>
      </Box>
    </Box>
  );
};

export default ValuePropCarousel;
